import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import {Configure, InfiniteHits, InstantSearch, SearchBox, SortBy,} from 'react-instantsearch';
import {searchClient} from '../lib/searchClient';
import SingleEstate from '../components/SingleEstate';
import {RangeSlider} from "./RangeSlider/RangeSlider";
import CustomCurrentRefinements from "./Widgets/CustomCurrentRefinements";
import CustomRefinementList from "./Widgets/CustomRefinementList";
import {FacetDropdown} from "./Widgets/FacetDropdown";
import SearchMap from "./Map/SearchMap";
import {useTranslation} from "react-i18next";
import {CustomSortBy} from "./Widgets/CustomSortBy";
import SaveSearch from "./Widgets/SaveSearch";
import ShowResultsButton from "./Widgets/ShowResultsButton";
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

export default function ListingsPage({defaultSort, defaultFilters}) {
    const {t, i18n} = useTranslation('common');
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [showMobileDefaultFilters, setShowMobileDefaultFilters] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [configureProps, setConfigureProps] = useState({
        hitsPerPage: 2000,
        enablePersonalization: true
    });
    const [currentIndex, setCurrentIndex] = useState('estates');

    useEffect(() => {
        const hasAdvanced = searchParams.has('advanced');
        setShowFilters(hasAdvanced);
        setShowMap(searchParams.has('map'));
        // Expand mobile filters when advanced search is enabled
        if (hasAdvanced) {
            setShowMobileDefaultFilters(true);
        }
    }, [searchParams]);

    useEffect(() => {
        const newConfigureProps = {
            hitsPerPage: 2000,
            enablePersonalization: true,
            filters: defaultFilters || '' // Ensure filters is always defined
        };

        setConfigureProps(newConfigureProps);
    }, [defaultFilters]);

    const toggleFilters = () => {
        const newShowFilters = !showFilters;
        setShowFilters(newShowFilters);
        if (newShowFilters) {
            navigate('?advanced');
        } else {
            navigate('.');
        }
    };

    const activeFiltersCount = () => {
        return 2;
    };

    const scrollToResults = () => {
        const resultsElement = document.getElementById('search-results');
        if (resultsElement) {
            resultsElement.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="bg-white">
            <InstantSearch
                indexName={currentIndex}
                searchClient={searchClient}
                routing={true}
            >
                <Configure {...configureProps} />
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="block md:flex items-baseline justify-between pb-6 pt-6">
                        <h1 className="text-4xl font-bold tracking-tight text-gray-900">{t('listings.title')}</h1>
                        <div className="hidden md:flex mt-6 md:mt-0 items-center justify-end">
                            <button
                                onClick={() => setShowMap((prevShowMap) => !prevShowMap)}
                                type="button"
                                className="inline-flex items-center gap-x-2 rounded-md bg-sky-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1.5"
                                    stroke="currentColor"
                                    className="h-5 w-5"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z"
                                    />
                                </svg>
                                {showMap ? t('listings.filters.mapToggle.hide') : t('listings.filters.mapToggle.show')}
                            </button>
                            <SaveSearch />
                        </div>
                    </div>
                </div>

                {/* Action buttons for mobile */}
                <div className="md:hidden mx-auto max-w-7xl px-4 pb-6">
                    <div className="grid grid-cols-2 gap-4">
                        <button
                            onClick={() => setShowMap((prevShowMap) => !prevShowMap)}
                            type="button"
                            className="flex items-center justify-center gap-x-2 rounded-md bg-sky-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-500"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                className="h-5 w-5"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z"
                                />
                            </svg>
                            {showMap ? t('listings.filters.mapToggle.hide') : t('listings.filters.mapToggle.show')}
                        </button>
                        <SaveSearch className="flex items-center justify-center gap-x-2 rounded-md bg-white border border-gray-300 px-3.5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 w-full ml-0" />
                    </div>
                </div>

                <div className={showMap ? '' : 'hidden'}>
                    <SearchMap></SearchMap>
                </div>

                <div className="bg-gray-50">
                    <div className="mx-auto max-w-7xl px-4 py-3 sm:flex sm:items-center sm:px-6 lg:px-8">
                        <button
                            type="button"
                            onClick={toggleFilters}
                            className="group flex text-sm items-center font-medium text-gray-700"
                        >
                            <svg className="mr-2 h-4 w-3 flex-none text-gray-400 group-hover:text-gray-500"
                                 viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd"
                                      d="M2.628 1.601C5.028 1.206 7.49 1 10 1s4.973.206 7.372.601a.75.75 0 0 1 .628.74v2.288a2.25 2.25 0 0 1-.659 1.59l-4.682 4.683a2.25 2.25 0 0 0-.659 1.59v3.037c0 .684-.31 1.33-.844 1.757l-1.937 1.55A.75.75 0 0 1 8 18.25v-5.757a2.25 2.25 0 0 0-.659-1.591L2.659 6.22A2.25 2.25 0 0 1 2 4.629V2.34a.75.75 0 0 1 .628-.74Z"
                                      clipRule="evenodd"/>
                            </svg>
                            {t('listings.filters.advancedSearch')}
                        </button>

                        <div aria-hidden="true" className="hidden h-5 w-px bg-gray-300 sm:ml-4 sm:block"/>

                        <div className="hidden sm:block mt-6 md:mt-2 sm:ml-4 sm:mt-0">
                            <div className="-m-1 flex flex-wrap items-center h-10">
                                <CustomCurrentRefinements/>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="hidden md:block max-w-7xl mx-auto px-4 py-8 bg-white lg:px-8">
                    <div className="grid md:grid-cols-4 gap-6">
                        <RangeSlider attribute="living_area" label={t('listings.rangeFilters.livingArea')}
                                     isRange={true}/>
                        <RangeSlider attribute="price" label={t('listings.rangeFilters.price')} isRange={true}/>

                        <FacetDropdown
                            attribute="type"
                            placeholder={t('listings.dropdowns.type.placeholder')}
                            title={t('listings.dropdowns.type.title')}
                        />

                        <FacetDropdown
                            attribute="parent_region_name"
                            placeholder={t('listings.dropdowns.region.placeholder')}
                            title={t('listings.dropdowns.region.title')}
                        />
                    </div>
                </div>

                <div className={showFilters ? 'bg-gray-100 mb-6' : 'hidden'}>

                    <div className="max-w-7xl mx-auto px-4 py-6  lg:px-8">
                        <div className="md:grid grid-cols-1 md:grid-cols-4 gap-6">
                            <RangeSlider attribute="construction_year_quarter" isRange={true}/>
                            <RangeSlider attribute="plot_area" isRange={true}/>
                            <RangeSlider attribute="terrace_area" isRange={true}/>
                            <div className="bg-gray-50 p-5 rounded-lg">
                                <fieldset>
                                    <legend className="block text-sm font-medium text-gray-900">
                                        {t('listings.filters.attributes.search')}
                                    </legend>
                                    <div className="mt-4">
                                        <SearchBox placeholder=""/>
                                    </div>
                                </fieldset>
                            </div>
                            <RangeSlider attribute="distance_to_beach" isRange={true}/>
                            <RangeSlider attribute="distance_to_golf" isRange={true}/>
                            <RangeSlider attribute="distance_to_airport" isRange={true}/>
                            <RangeSlider attribute="distance_to_center" isRange={true}/>
                            <div className="col-span-2">
                                <CustomRefinementList
                                    attribute="private_features"
                                    title={t('estate.details.features.private_features', 'Private Features')}
                                    gridColumns={2}
                                />
                            </div>
                            <div className="col-span-2">
                                <CustomRefinementList
                                    attribute="community_features"
                                    title={t('estate.details.features.community_features', 'Community Features')}
                                    gridColumns={2}
                                />
                            </div>
                            <CustomRefinementList attribute="bathrooms"
                                                  title={t('listings.propertyFeatures.bathrooms')}/>
                            <CustomRefinementList attribute="bedrooms"
                                                  title={t('listings.propertyFeatures.bedrooms')}/>
                            <CustomRefinementList attribute="status"
                                                  title="Status"/>
                            <div></div>

                        </div>
                    </div>
                </div>

                <ShowResultsButton
                    isVisible={showFilters}
                    onShowResults={scrollToResults}
                />
                <div className="max-w-7xl mx-auto px-4 py-8 bg-white lg:px-8 lg:pt-0" id="search-results">
                    <div className="col-span-3">
                        <div className="flex justify-between items-center mb-6">
                            <div className="md:hidden">
                                <button
                                    type="button"
                                    onClick={() => setShowMobileDefaultFilters(!showMobileDefaultFilters)}
                                    className={`flex items-center gap-1 text-sm font-medium ${showMobileDefaultFilters ? 'bg-sky-600 text-white' : 'bg-white text-sky-600'} px-3 py-2 rounded-md shadow-sm border border-gray-300 h-[38px]`}
                                >
                                    <AdjustmentsHorizontalIcon className="h-5 w-5" />
                                    <span className="whitespace-nowrap">{showMobileDefaultFilters ? t('listings.filters.hideFilters') : t('listings.filters.showFilters')}</span>
                                </button>
                            </div>
                            <div className="flex items-center">
                                <CustomSortBy
                                    items={[
                                        { label: t('listings.sorting.newest'), value: 'estates', name: 'estates' },
                                        { 
                                            label: t('listings.sorting.mostPopular'), 
                                            value: 'estate_total_views', 
                                            name: 'estate_total_views' 
                                        },
                                        { 
                                            label: t('listings.sorting.priceHighToLow'), 
                                            value: 'estates_price_desc', 
                                            name: 'estates_price_desc' 
                                        },
                                        { 
                                            label: t('listings.sorting.priceLowToHigh'), 
                                            value: 'estates_price_asc', 
                                            name: 'estates_price_asc' 
                                        },
                                    ]}
                                    onIndexChange={setCurrentIndex}
                                />
                            </div>
                        </div>

                        {/* Mobile active filters */}
                        <div className="md:hidden mb-4 overflow-x-auto pb-2 -mx-4 px-4">
                            <div className="flex flex-nowrap">
                                <CustomCurrentRefinements />
                            </div>
                        </div>

                        {/* Mobile filters section */}
                        <div className={classNames(
                            "md:hidden mb-6",
                            showMobileDefaultFilters ? 'block' : 'hidden'
                        )}>
                            <div className="grid grid-cols-1 gap-4">
                                <RangeSlider attribute="living_area" label={t('listings.rangeFilters.livingArea')}
                                            isRange={true}/>
                                <RangeSlider attribute="price" label={t('listings.rangeFilters.price')} isRange={true}/>

                                <FacetDropdown
                                    attribute="type"
                                    placeholder={t('listings.dropdowns.type.placeholder')}
                                    title={t('listings.dropdowns.type.title')}
                                />

                                <FacetDropdown
                                    attribute="parent_region_name"
                                    placeholder={t('listings.dropdowns.region.placeholder')}
                                    title={t('listings.dropdowns.region.title')}
                                />
                                
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowMobileDefaultFilters(false);
                                    }}
                                    className="w-full bg-sky-600 text-white font-medium py-3 px-4 rounded-md shadow-sm hover:bg-sky-700"
                                >
                                    {t('listings.filters.showResults')}
                                </button>
                            </div>
                        </div>

                        <InfiniteHits
                            hitComponent={({hit}) => (
                                <Link to={`/estate/${hit.id}`} key={'estate' + hit.id}>
                                    <SingleEstate estate={hit}/>
                                </Link>
                            )}
                            classNames={{
                                root: 'w-full',
                                list: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6',
                            }}
                        />
                    </div>
                </div>
            </InstantSearch>
        </div>
    );
}